import React, { useState } from 'react';
import { ScrollText, Shield, CreditCard, Eye, Loader2, Check, AlertCircle } from 'lucide-react';
import { useWarrantyGcOptions, useAssignWarrantyGc } from '../hooks/useWarranties';
import { warrantyApi } from '../api/warrantyApi';
import type { Warranty } from '../types/warranty.types';

interface WarrantyGcCardProps {
  warranty: Warranty;
  /** Affiche le sélecteur Stripe (si la société a Stripe activé) */
  hasStripePayment: boolean;
  /** L'utilisateur peut-il modifier les CG ? */
  canEdit: boolean;
}

/**
 * Bloc « Conditions Générales » sur la page détail garantie.
 * Permet d'assigner individuellement une version de CG (cash et/ou stripe).
 * NULL = défaut Affinicar.
 */
export const WarrantyGcCard: React.FC<WarrantyGcCardProps> = ({
  warranty,
  hasStripePayment,
  canEdit,
}) => {
  return (
    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-4 tw-mb-4">
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
        <ScrollText className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
        <h3 className="tw-font-semibold tw-text-gray-900 tw-mb-0">Conditions Générales</h3>
      </div>
      <p className="tw-text-xs tw-text-gray-500 tw-mb-3 tw-mb-0">
        Choisissez quelle version de CG sera utilisée pour les contrats de cette garantie.
        « Par défaut Affinicar » = aucune assignation explicite.
      </p>

      <div className="tw-space-y-4 tw-mt-3">
        <GcVariantSelector
          warranty={warranty}
          variant="cash"
          icon={<Shield className="tw-w-3.5 tw-h-3.5" />}
          label="Variante cash"
          canEdit={canEdit}
        />
        {hasStripePayment && (
          <GcVariantSelector
            warranty={warranty}
            variant="stripe"
            icon={<CreditCard className="tw-w-3.5 tw-h-3.5" />}
            label="Variante Stripe"
            canEdit={canEdit}
          />
        )}
      </div>
    </div>
  );
};

interface GcVariantSelectorProps {
  warranty: Warranty;
  variant: 'cash' | 'stripe';
  icon: React.ReactNode;
  label: string;
  canEdit: boolean;
}

const GcVariantSelector: React.FC<GcVariantSelectorProps> = ({
  warranty,
  variant,
  icon,
  label,
  canEdit,
}) => {
  const { data: options, isLoading } = useWarrantyGcOptions(warranty.uuid, variant);
  const assignMutation = useAssignWarrantyGc();
  const [feedback, setFeedback] = useState<'idle' | 'saved' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const currentValue =
    variant === 'cash'
      ? warranty.v2GeneralConditionsIdCash ?? ''
      : warranty.v2GeneralConditionsIdStripe ?? '';

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newId = e.target.value || null;
    const dto =
      variant === 'cash'
        ? { v2GeneralConditionsIdCash: newId }
        : { v2GeneralConditionsIdStripe: newId };
    try {
      setFeedback('idle');
      setErrorMsg(null);
      await assignMutation.mutateAsync({ uuid: warranty.uuid, dto });
      setFeedback('saved');
      setTimeout(() => setFeedback('idle'), 2000);
    } catch (err: any) {
      setFeedback('error');
      setErrorMsg(err?.response?.data?.message || err?.message || 'Erreur d\'assignation');
    }
  };

  const handlePreview = async () => {
    try {
      const blob = await warrantyApi.downloadConditionsPdf(warranty.uuid, variant);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Note : pas de revoke immédiat pour laisser le navigateur charger la ressource
    } catch (err: any) {
      alert(`Erreur preview PDF : ${err?.message || 'inconnue'}`);
    }
  };

  return (
    <div>
      <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1">
        {icon}
        {label}
      </label>
      <div className="tw-flex tw-items-center tw-gap-2">
        <select
          value={currentValue}
          onChange={handleChange}
          disabled={!canEdit || isLoading || assignMutation.isLoading}
          className="tw-flex-1 tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm focus:tw-ring-2 focus:tw-ring-affinicar-primary focus:tw-border-transparent disabled:tw-bg-gray-50"
        >
          <option value="">
            Par défaut Affinicar
            {options?.defaultVersion ? ` (v${options.defaultVersion.version} – ${options.defaultVersion.cgDate})` : ''}
          </option>
          {options?.versions?.map((v) => (
            <option key={v.id} value={v.id}>
              v{v.version} — {v.versionName ?? 'sans nom'} ({v.cgDate})
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={handlePreview}
          className="tw-p-2 tw-border tw-border-gray-300 tw-text-gray-700 tw-rounded-md hover:tw-bg-gray-50 tw-transition-colors"
          title="Voir le PDF"
        >
          <Eye className="tw-w-4 tw-h-4" />
        </button>
      </div>
      {isLoading && (
        <div className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-500 tw-mt-1">
          <Loader2 className="tw-w-3 tw-h-3 tw-animate-spin" />
          Chargement…
        </div>
      )}
      {feedback === 'saved' && (
        <div className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-green-700 tw-mt-1">
          <Check className="tw-w-3 tw-h-3" />
          Enregistré
        </div>
      )}
      {feedback === 'error' && errorMsg && (
        <div className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-red-700 tw-mt-1">
          <AlertCircle className="tw-w-3 tw-h-3" />
          {errorMsg}
        </div>
      )}
    </div>
  );
};
